import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { FaInstagram, FaTiktok, FaGoodreads, FaAmazon } from "react-icons/fa";
import Newsletter from "../newsletter/Newsletter";
import Footer from "../privacy-policy/Footer";

const Parallax = styled.div`
  background-image: url("/images/parallax.jpg");
  background-attachment: fixed;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  padding-top: 10px;
`;

const ContactContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 40px;
  margin: clamp(20px, 5vw, 40px);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 30px;
  }
`;

const ClipedBox = styled.div`
  background-color: rgba(255, 255, 255, 0.66);
  mask: radial-gradient(40px at 40px 40px, transparent 98%, black) -40px -40px;
  padding: clamp(20px, 5vw, 70px);
  border-radius: clamp(15px, 3vw, 20px);
  max-width: 350px;
`;

const Title = styled.h1`
  color: #0360ffa2;
  text-align: center;
  font-family: 'MedievalSharp', serif;
  font-size: clamp(1.5rem, 5vw, 2.8rem);
  padding-bottom: clamp(15px, 3vw, 25px);
`;

const Paragraph = styled.p`
  color: #2b60bdff;
  font-family: 'Mozilla Text', serif;
  font-size: clamp(1rem, 2vw, 1.5rem);
  line-height: 1.6;
`;

const FormContainer = styled.div`
  flex: 1;
  width: 100%;
  min-width: 330px;
  border: 4px solid white;
  border-radius: 20px;
  padding: 10px;

  form {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  label {
    color: white;
    font-family: 'Mozilla Text', serif;
    font-size: clamp(1rem, 2vw, 1.5rem);
  }

  input {
    padding: 10px;
    font-family: 'Mozilla Text', serif;
    font-size: clamp(1rem, 2vw, 1.5rem);
    border-radius: 10px;
    border: 2px solid #caaeecff;
    background: rgba(255,255,255,0.85);

    &:focus {
      outline: none;
      border-color: #ba9debbb;
    }
  }

  textarea {
    padding: 10px;
    font-family: 'Mozilla Text', serif;
    font-size: clamp(1rem, 2vw, 1.5rem);
    border-radius: 10px;
    border: 2px solid #caaeecff;
    background: rgba(255,255,255,0.85);
    overflow-y: scroll;
    height: 150px;
    resize: none;

    &:focus {
      outline: none;
      border-color: #ba9debbb;
    }
  }

  button {
    width: 100%;
  }
`;

const Button = styled.button`
  background-color: #caaeecff;
  color: white;
  font-family: 'Mozilla Text', serif;
  font-size: clamp(1rem, 2vw, 1.5rem);
  font-weight: bold;
  align-self: center;
  margin-top: 15px;
  padding: 10px;
  border: none;
  border-radius: 15px;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #ba9debbb;
  }
`;

const FullBox = styled.div`
  background-color: rgba(255, 255, 255, 0.66);
  padding: clamp(15px, 5vw, 60px);
`;

const ArcContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 50px;

  @media (max-width: 1300px) {
    flex-direction: column;
    gap: 30px;
  }
`;

const List = styled.ul`
  color: #2b60bdff;
  font-family: 'Mozilla Text', serif;
  font-size: clamp(1rem, 2vw, 1.5rem);
  line-height: 1.6;
  margin-left: 20px;
`;

const SpecialText = styled.p`
  font-weight: bold;
  font-style: italic;
`;

const CarouselContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const Arrow = styled.button`
  height: fit-content;
  background: none;
  border: none;
  font-size: clamp(2rem, 2.5vw + 1rem, 4rem);
  font-weight: bold;
  color: #3695f4ff;
  cursor: pointer;

  &:hover {
    color: #0360ffa2;
    transform: scale(1.08);
  }
`;

const CarouselImage = styled.img`
  width: 200px;
  height: 300px;
  border-radius: 10px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: rgb(38, 57, 77) 0px 20px 30px -10px;
  cursor: pointer;

  &:hover {
    transform: scale(1.02);
    box-shadow: rgb(38, 57, 77) 0px 20px 40px -10px;
  }

  @media (max-width: 850px) {
    width: 100px;
    height: 170px;
  }
`;

const ArcInfo = styled.div`
  display: inline-flex; 
  flex-direction: column; 
  align-items: center;
  white-space: nowrap;   
  max-width: 250px;     
`;

const ConnectContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 50px;
  margin: clamp(15px, 5vw, 30px);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 30px;
  }
`;

const LinksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: row;
  }
`;

const Link = styled.a`
  color: white;

  svg {
    width: clamp(40px, 5vw, 60px);
    height: auto;
  }

  &:hover {
    color: rgba(255, 255, 255, 0.66);
    transform: scale(1.09);
  }
`;

const bookCovers = [
  {
    src: "/images/b1.jfif",
    alt: "Beyond Prejudice by Anna Miki",
    url: "/beyond-prejudice",
    releaseDate: "16 Nov, 2024",
    arcStatus: "closed"
  },
  {
    src: "/images/b2.jpg",
    alt: "When the Butterflies Are Gone by Anna Miki",
    url: "/when-the-butterflies-are-gone",
    releaseDate: "10 Dec, 2025",
    arcStatus: "closed"
  }
];

const Contact = () => {
  useEffect(() => {
    document.title = "Contact - Anna Miki";
  }, []);

  const [index, setIndex] = React.useState(0);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();

    const payload = { name, email, subject, message };

    setName("");
    setEmail("");
    setSubject("");
    setMessage("");

    fetch("https://anna-miki-backend.onrender.com/api/contact", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).catch((err) => {
      console.error("Background contact error:", err);
    });
  };

  React.useEffect(() => {
      const interval = setInterval(() => {
        setIndex(prev => (prev + 1) % bookCovers.length);
      }, 4000);
  
      return () => clearInterval(interval);
    }, []);
  
  return (
    <Parallax>

      <ContactContainer>

        <ClipedBox>

          <Title>Contact Anna</Title>

          <Paragraph style={{ textAlign: "center" }}>
            Questions or professional inquiries are always appreciated.
            You can use the contact form to send a message, and the author will get
            back to you as soon as possible.
          </Paragraph>

        </ClipedBox>

        <FormContainer>

          <form onSubmit={handleSubmit}>
            
            <label>Name*:</label>
            <input 
              type="text"
              placeholder="Enter your name..."
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />

            <label>Email*:</label>
            <input 
              type="email"
              placeholder="Enter your email..."
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <label>Subject*:</label>
            <input 
              type="text"
              placeholder="Enter your subject..."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
            />

            <label>Message*:</label>
            <textarea 
              placeholder="Enter your message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
            />

            <Button type="submit">Send</Button>

          </form>

        </FormContainer>

      </ContactContainer>

      <FullBox>

        <ArcContainer>

          <div>

            <Title>Request an ARC</Title>

            <Paragraph>
              An Advance Reader Copy (ARC) is a pre-publication version of a
              book shared with early readers to help build visibility and gather reviews
              before release. ARC readers are an essential part of launching a book and
              your support is truly appreciated.
            </Paragraph> <br/>

            <List>
              <li>ARCs are limited and not guaranteed</li>
              <li>There is no financial compensation for reviews</li>
              <li>Requesting an ARC means agreeing to read the book and post an honest review on Amazon, Goodreads, and/or social media around the release date</li>
            </List> <br/>

            <Paragraph>
              <SpecialText>To stay updated on new releases and ARC openings, be sure to subscribe to the newsletter!</SpecialText>
            </Paragraph>

          </div>

          <CarouselContainer>

            <Arrow onClick={() => setIndex(prev => prev === 0 ? bookCovers.length - 1 : prev - 1)}>❮</Arrow>

            <a href={bookCovers[index].url} target="_self" rel="noopener noreferrer">
              <CarouselImage src={bookCovers[index].src} alt={bookCovers[index].alt} />
            </a>

            <ArcInfo>

              <Paragraph><strong>Release:</strong> {bookCovers[index].releaseDate}</Paragraph>
              <Paragraph><strong>ARC Status:</strong> {bookCovers[index].arcStatus === "open" ? "Open" : "Closed"}</Paragraph>

              {bookCovers[index].arcStatus === "open" && (
                <a 
                  href={bookCovers[index].request} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none" }}
                >
                  <Button>Request ARC</Button>
                </a>
              )}

            </ArcInfo>

            <Arrow onClick={() => setIndex(prev => (prev + 1) % bookCovers.length)}>❯</Arrow>
          
          </CarouselContainer>

        </ArcContainer>

      </FullBox>

      <ConnectContainer>

        <LinksContainer>

          <Link href="https://www.instagram.com/annamiki_author/" target="_blank" rel="noopener noreferrer">
            <FaInstagram />
          </Link>

          <Link href="https://www.tiktok.com/@annamiki84" target="_blank" rel="noopener noreferrer">
            <FaTiktok />
          </Link>

          <Link href="https://www.goodreads.com/author/show/51714605.Anna_Miki" target="_blank" rel="noopener noreferrer">
            <FaGoodreads />
          </Link>

          <Link href="https://www.amazon.com/stores/Anna-Miki/author/B0DKYMFFFN" target="_blank" rel="noopener noreferrer">
            <FaAmazon />
          </Link>

        </LinksContainer>

        <Newsletter />

      </ConnectContainer>

      <Footer />

    </Parallax>
  );
};

export default Contact;
